/**
 * Governor commands — sherwood governor <subcommand>
 *
 * View and manage per-vault SyndicateGovernor parameters. Each vault has
 * its own governor (deployed by the factory as a BeaconProxy), so every
 * command takes `--vault` and the parameter setters are vault-owner-only.
 * Protocol-wide fees (set-protocol-fee) live on ProtocolConfig instead.
 */

import { Command } from "commander";
import chalk from "chalk";
import ora from "ora";
import { isAddress, type Address } from "viem";
import { getExplorerUrl, getChain } from "../lib/network.js";
import { formatContractError } from "../lib/errors.js";
import { isCalldataOnly, emitCalldata } from "../lib/calldata.js";
import {
  encodeGovernorParam,
  encodeProtocolFeeBps,
  type ChainId,
  type GovernorParam,
} from "@sherwoodagent/sdk";
import {
  getGovernorOf,
  getGovernorParams,
  getProtocolConfigAddress,
  setVotingPeriod,
  setExecutionWindow,
  setVetoThresholdBps,
  setProtocolFeeBps,
  setMaxPerformanceFeeBps,
  setMaxStrategyDuration,
  setCooldownPeriod,
} from "../lib/governor.js";
import { formatDurationLong as formatDuration, parseBigIntArg } from "../lib/format.js";

const G = chalk.green;
const W = chalk.white;
const DIM = chalk.gray;
const BOLD = chalk.white.bold;
const LABEL = chalk.green.bold;
const SEP = () => console.log(DIM("─".repeat(60)));

function parseVaultArg(raw: unknown): Address {
  const vault = String(raw);
  if (!isAddress(vault)) {
    console.error(chalk.red(`Invalid vault address: ${vault}`));
    process.exit(1);
  }
  return vault as Address;
}

/**
 * Register one vault-owner parameter setter. Per-vault governor: setters
 * apply immediately on vault-owner call and revert with
 * `ParamsFrozenDuringProposal` while any proposal is open on the vault.
 * With `--calldata-only` it prints the unsigned `setX(value)` calldata via
 * the shared SDK encoder instead, already targeting the vault's governor
 * (resolved on the fly via `factory.governorOf(vault)`).
 */
function registerParamSetter(
  governor: Command,
  spec: {
    command: string;
    description: string;
    flag: string;
    flagDescription: string;
    valueLabel: string;
    param: GovernorParam;
    setter: (vault: Address, value: bigint) => Promise<`0x${string}`>;
    spinnerText: string;
    succeed: (rawValue: string) => string;
    failText: string;
  },
): void {
  governor
    .command(spec.command)
    .description(spec.description)
    .requiredOption("--vault <address>", "Vault whose governor to configure")
    .requiredOption(spec.flag, spec.flagDescription)
    .action(async (opts) => {
      // `valueLabel` ("seconds" | "bps") matches the camelCased option key.
      const rawValue = String(opts[spec.valueLabel]);
      const vault = parseVaultArg(opts.vault);

      if (isCalldataOnly()) {
        try {
          const value = parseBigIntArg(rawValue, spec.valueLabel);
          emitCalldata(
            encodeGovernorParam(
              { param: spec.param, value, governor: await getGovernorOf(vault) },
              getChain().id as ChainId,
            ),
          );
        } catch (err) {
          console.error(chalk.red(formatContractError(err)));
          process.exit(1);
        }
        return;
      }

      const spinner = ora(spec.spinnerText).start();
      try {
        const hash = await spec.setter(vault, parseBigIntArg(rawValue, spec.valueLabel));
        spinner.succeed(G(spec.succeed(rawValue)));
        console.log(DIM(`  ${getExplorerUrl(hash)}`));
      } catch (err) {
        spinner.fail(spec.failText);
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });
}

export function registerGovernorCommands(program: Command): void {
  const governor = program.command("governor").description("Per-vault governor parameters");

  // ── governor info ──

  governor
    .command("info")
    .description("Display a vault's governor address and parameters")
    .requiredOption("--vault <address>", "Vault whose governor to inspect")
    .action(async (opts) => {
      const vault = parseVaultArg(opts.vault);
      const spinner = ora("Loading governor info...").start();
      try {
        const [governorAddress, params] = await Promise.all([
          getGovernorOf(vault),
          getGovernorParams(vault),
        ]);

        spinner.stop();

        console.log();
        console.log(LABEL("  ◆ Governor Parameters"));
        SEP();
        console.log(W(`  Vault:                ${G(vault)}`));
        console.log(W(`  Governor:             ${G(governorAddress)}`));
        console.log(W(`  Voting Period:        ${BOLD(formatDuration(params.votingPeriod))}`));
        console.log(W(`  Execution Window:     ${BOLD(formatDuration(params.executionWindow))}`));
        console.log(W(`  Veto Threshold:         ${BOLD(`${Number(params.vetoThresholdBps) / 100}%`)}`));
        console.log(W(`  Max Performance Fee:  ${BOLD(`${Number(params.maxPerformanceFeeBps) / 100}%`)}`));
        console.log(W(`  Max Strategy Duration:${BOLD(` ${formatDuration(params.maxStrategyDuration)}`)}`));
        console.log(W(`  Cooldown Period:      ${BOLD(formatDuration(params.cooldownPeriod))}`));

        SEP();
        console.log();
      } catch (err) {
        spinner.fail("Failed to load governor info");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });

  // ── governor parameter setters (vault-owner only, per-vault) ──

  registerParamSetter(governor, {
    command: "set-voting-period",
    description: "Set the voting period (vault owner only)",
    flag: "--seconds <n>",
    flagDescription: "New voting period in seconds",
    valueLabel: "seconds",
    param: "votingPeriod",
    setter: setVotingPeriod,
    spinnerText: "Setting voting period...",
    succeed: (v) => `Voting period updated to ${v}s (reverts if the vault has an open proposal).`,
    failText: "Failed to set voting period",
  });

  registerParamSetter(governor, {
    command: "set-execution-window",
    description: "Set the execution window (vault owner only)",
    flag: "--seconds <n>",
    flagDescription: "New execution window in seconds",
    valueLabel: "seconds",
    param: "executionWindow",
    setter: setExecutionWindow,
    spinnerText: "Setting execution window...",
    succeed: (v) => `Execution window updated to ${v}s (reverts if the vault has an open proposal).`,
    failText: "Failed to set execution window",
  });

  registerParamSetter(governor, {
    command: "set-veto-threshold",
    description: "Set the veto threshold in bps (vault owner only)",
    flag: "--bps <n>",
    flagDescription: "New veto threshold in bps (e.g. 4000 = 40%)",
    valueLabel: "bps",
    param: "vetoThresholdBps",
    setter: setVetoThresholdBps,
    spinnerText: "Setting veto threshold...",
    succeed: (v) => `Veto threshold updated to ${Number(v) / 100}% (reverts if the vault has an open proposal).`,
    failText: "Failed to set veto threshold",
  });

  registerParamSetter(governor, {
    command: "set-max-fee",
    description: "Set the max performance fee in bps (vault owner only)",
    flag: "--bps <n>",
    flagDescription: "New max fee in bps (e.g. 3000 = 30%)",
    valueLabel: "bps",
    param: "maxPerformanceFeeBps",
    setter: setMaxPerformanceFeeBps,
    spinnerText: "Setting max fee...",
    succeed: (v) => `Max performance fee updated to ${Number(v) / 100}% (reverts if the vault has an open proposal).`,
    failText: "Failed to set max fee",
  });

  registerParamSetter(governor, {
    command: "set-max-duration",
    description: "Set the max strategy duration in seconds (vault owner only)",
    flag: "--seconds <n>",
    flagDescription: "New max duration in seconds",
    valueLabel: "seconds",
    param: "maxStrategyDuration",
    setter: setMaxStrategyDuration,
    spinnerText: "Setting max duration...",
    succeed: (v) => `Max strategy duration updated to ${v}s (reverts if the vault has an open proposal).`,
    failText: "Failed to set max duration",
  });

  registerParamSetter(governor, {
    command: "set-cooldown",
    description: "Set the cooldown period in seconds (vault owner only)",
    flag: "--seconds <n>",
    flagDescription: "New cooldown in seconds",
    valueLabel: "seconds",
    param: "cooldownPeriod",
    setter: setCooldownPeriod,
    spinnerText: "Setting cooldown...",
    succeed: (v) => `Cooldown period updated to ${v}s (reverts if the vault has an open proposal).`,
    failText: "Failed to set cooldown",
  });

  // ── governor set-protocol-fee (ProtocolConfig, protocol-owner only) ──
  //
  // Per-vault governor refactor: fee setters moved off the governor to the
  // protocol-wide ProtocolConfig contract (`factory.protocolConfig()`), so
  // this command takes no --vault.

  governor
    .command("set-protocol-fee")
    .description("Set the protocol fee in bps on ProtocolConfig (protocol owner only)")
    .requiredOption("--bps <n>", "New protocol fee in bps (e.g. 500 = 5%, max 1000 = 10%)")
    .action(async (opts) => {
      const rawValue = String(opts.bps);

      if (isCalldataOnly()) {
        try {
          const value = parseBigIntArg(rawValue, "bps");
          // `protocolFeeBps` lives on the protocol-wide ProtocolConfig, not a
          // per-vault governor — the dedicated encoder targets the config
          // resolved via `factory.protocolConfig()`.
          emitCalldata(
            encodeProtocolFeeBps(
              { protocolConfig: await getProtocolConfigAddress(), value },
              getChain().id as ChainId,
            ),
          );
        } catch (err) {
          console.error(chalk.red(formatContractError(err)));
          process.exit(1);
        }
        return;
      }

      const spinner = ora("Setting protocol fee...").start();
      try {
        const hash = await setProtocolFeeBps(parseBigIntArg(rawValue, "bps"));
        const configAddress = await getProtocolConfigAddress();
        spinner.succeed(
          G(`Protocol fee updated to ${Number(rawValue) / 100}% on ProtocolConfig (${configAddress}).`),
        );
        console.log(DIM(`  ${getExplorerUrl(hash)}`));
      } catch (err) {
        spinner.fail("Failed to set protocol fee");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });
}
